/**
 * Timeline Class
 * Manages the timeline, tracks collection, viewport state, and region groups
 */
export class Timeline {
  constructor(projectSampleRate = null) {
    this.tracks = [];
    this.duration = 0;
    this.viewportStartTime = 0;
    this.viewportEndTime = 10;
    this.pixelsPerSecond = 50;
    this.viewportScrollTime = 0;
    this.projectSampleRate = projectSampleRate; // Will be set from first track if null

    // Region groups - multiple named groups of regions
    // Regions store: { start, end, startSample, endSample }
    // start/end are in seconds, startSample/endSample are in projectSampleRate
    this.regionGroups = new Map();
    this.activeRegionGroupId = null; // Currently active group
    this.regionGroupIdCounter = 0; // For generating unique IDs

    // Legacy selection state (kept for backward compatibility during transition)
    this.selection = null; // { start: number, end: number, startSample, endSample } or null
  }

  /**
   * Set the project sample rate (called when first track is added)
   * @param {number} sampleRate - Sample rate in Hz
   */
  setProjectSampleRate(sampleRate) {
    if (this.projectSampleRate === null) {
      this.projectSampleRate = sampleRate;
    }
  }

  /**
   * Convert time to samples using project sample rate
   * @param {number} time - Time in seconds
   * @returns {number} Sample position
   */
  timeToSamples(time) {
    if (!this.projectSampleRate) return 0;
    return Math.round(time * this.projectSampleRate);
  }

  /**
   * Convert samples to time using project sample rate
   * @param {number} samples - Sample position
   * @returns {number} Time in seconds
   */
  samplesToTime(samples) {
    if (!this.projectSampleRate) return 0;
    return samples / this.projectSampleRate;
  }

  getEffectiveDuration(canvasWidth) {
    const minDurationFromZoom = canvasWidth / this.pixelsPerSecond;
    return Math.max(this.duration, minDurationFromZoom);
  }

  updateViewport(canvasWidth) {
    const visibleDuration = canvasWidth / this.pixelsPerSecond;
    this.viewportStartTime = this.viewportScrollTime;
    this.viewportEndTime = this.viewportStartTime + visibleDuration;
  }

  timeToCanvasX(time) {
    return (time - this.viewportStartTime) * this.pixelsPerSecond;
  }

  canvasXToTime(x) {
    return this.viewportStartTime + (x / this.pixelsPerSecond);
  }

  getVisibleDuration() {
    return this.viewportEndTime - this.viewportStartTime;
  }

  scroll(deltaTime, canvasWidth) {
    this.viewportScrollTime += deltaTime;
    const visibleDuration = this.getVisibleDuration();
    const effectiveDuration = this.getEffectiveDuration(canvasWidth);
    const maxScroll = Math.max(0, effectiveDuration - visibleDuration);
    this.viewportScrollTime = Math.max(0, Math.min(maxScroll, this.viewportScrollTime));
  }

  // Region group management
  createRegionGroup(name = '', options = {}) {
    const groupId = `region-group-${this.regionGroupIdCounter++}`;
    this.regionGroups.set(groupId, {
      name,
      regions: [],
      maxRegions: options.maxRegions !== undefined ? options.maxRegions : null, // null = unlimited
      readOnly: options.readOnly !== undefined ? options.readOnly : false
    });
    return groupId;
  }

  getActiveRegionGroup() {
    if (!this.activeRegionGroupId) return null;
    return this.regionGroups.get(this.activeRegionGroupId);
  }

  addRegionToActiveGroup(start, end) {
    if (!this.activeRegionGroupId) {
      // Create default group if none exists
      this.activeRegionGroupId = this.createRegionGroup('default');
    }

    const group = this.regionGroups.get(this.activeRegionGroupId);
    if (!group) return;

    // Check if group is read-only
    if (group.readOnly) {
      console.warn('Cannot add region: group is read-only');
      return;
    }

    // Check region limit
    if (group.maxRegions !== null && group.regions.length >= group.maxRegions) {
      console.warn(`Cannot add region: maximum of ${group.maxRegions} region(s) reached`);
      return;
    }

    // Add new region with both time and sample data
    const startSample = this.timeToSamples(start);
    const endSample = this.timeToSamples(end);
    group.regions.push({
      start,
      end,
      startSample,
      endSample
    });

    // Merge overlapping regions
    this.mergeOverlappingRegions(this.activeRegionGroupId);
  }

  mergeOverlappingRegions(groupId) {
    const group = this.regionGroups.get(groupId);
    if (!group || group.regions.length === 0) return;

    // Sort regions by start time
    group.regions.sort((a, b) => a.start - b.start);

    // Merge overlapping regions
    const merged = [];
    let current = { ...group.regions[0] };

    for (let i = 1; i < group.regions.length; i++) {
      const region = group.regions[i];

      if (region.start <= current.end) {
        // Overlapping or adjacent - merge
        current.end = Math.max(current.end, region.end);
        current.endSample = Math.max(current.endSample || 0, region.endSample || 0);
      } else {
        // Not overlapping - push current and start new
        merged.push(current);
        current = { ...region };
      }
    }

    merged.push(current);
    group.regions = merged;
  }

  removeRegionFromActiveGroup(index) {
    if (!this.activeRegionGroupId) return;
    const group = this.regionGroups.get(this.activeRegionGroupId);
    if (!group) return;
    const region = group.regions[index];
    group.regions.splice(index, 1);
    if(region === this.selection) {
      this.selection = null;
    }
  }

  getActiveRegions() {
    const group = this.getActiveRegionGroup();
    return group ? group.regions : [];
  }
}
